import { useEffect, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Mutation } from '@/utils/react-query/Mutations';
import Toast from 'react-native-toast-message';

const SaveShortcutModal = ({ isOpen, onClose, personID, screen, icon = 'bookmark-outline', library = 'ionicon', label }) => {
    const [name, setName] = useState(label ?? '');
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        setName(label ?? '');
    }, [label]);

    const saveShortcutMutation = Mutation.shortcuts.save({
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.shortcuts.byPersonId(data.personID) });
            Toast.show({ type: 'success', text2: 'Shortcut saved!' });
            setName(label ?? '');
            onClose();
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to save shortcut' });
        },
        onSettled: () => setLoading(false)
    });

    const handleSave = () => {
        if (!name.trim()) {
            Toast.show({ type: 'error', text2: 'Please enter a name for the shortcut' });
            return;
        }

        setLoading(true);
            
        saveShortcutMutation.mutate({
            personID,
            screen,
            label: name.trim(),
            icon,
            library
        });
    };

    const handleClose = () => {
        setName(label ?? '');
        onClose();
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Save Shortcut"
            cancelText="Cancel"
            confirmText="Save"
            onConfirm={handleSave}
            loading={loading}
        >
            <View style={styles.container}>
                <View style={styles.inputGroup}>
                    <Text style={styles.label}>Name</Text>
                    <TextInput
                        style={styles.input}
                        value={name}
                        onChangeText={setName}
                        placeholder="Enter shortcut name"
                        placeholderTextColor={styles.placeholder.color}
                        autoFocus
                    />
                </View>

                <View style={styles.inputGroup}>
                    <Text style={styles.label}>Destination</Text>
                    <View style={styles.destinationBox}>
                        <Text style={styles.destinationText} numberOfLines={2}>{screen}</Text>
                    </View>
                </View>
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        gap: 16,
        paddingVertical: 8,
    },
    inputGroup: {
        gap: 6,
    },
    label: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    input: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 12,
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        backgroundColor: colors.surface,
    },
    placeholder: {
        color: colors.textSecondary,
    },
    destinationBox: {
        backgroundColor: colors.surfaceVariant,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: colors.border,
    },
    destinationText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
}));

export default SaveShortcutModal;
